import { GroupSubject } from '@bp/pim-auth-permissions';
import { GridColumn } from '@bp/ui-components';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { BpCard } from 'components/BpCard/BpCard';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { GroupsPreviewTable } from '../GroupsPreviewTable/GroupsPreviewTable';
import { CoursesPreviewTable } from '../CoursesPreviewTable/CoursesPreviewTable';

const PreviewDataQueryFieldsDocument = graphql(`
  fragment PreviewDataQueryFields on Query {
    profileCourses: groups(
      where: {
        type: course
        organization: { uuid_IN: $organizations }
        AND: [
          { OR: [{ editors_SOME: { uuid: $profile } }, { viewers_SOME: { uuid: $profile } }] }
          { OR: [{ inCurrentSchoolyear: true }, { isCommunity: true }] }
        ]
      }
    ) {
      uuid
      __typename
    }
    profileGroups: groups(
      where: {
        type: group
        organization: { uuid_IN: $organizations }
        OR: [{ editors_SOME: { uuid: $profile } }, { viewers_SOME: { uuid: $profile } }]
      }
    ) {
      uuid
      __typename
    }
    ...GroupsPreviewTableFields
    ...CoursesPreviewTableFields
  }
`);

type PreviewDataProps = {
  query?: FragmentType<typeof PreviewDataQueryFieldsDocument>;
  isCourseEditor: (group: GroupSubject) => boolean | undefined;
};

export function PreviewData({ query, isCourseEditor }: PreviewDataProps) {
  const data = getFragmentData(PreviewDataQueryFieldsDocument, query);
  const { profileCourses, profileGroups } = data ?? {};
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleCourseNavigate = (uuid: string): void => {
    navigate('/courses/' + uuid);
  };

  const handleGroupNavigate = (uuid: string): void => {
    navigate('/groups/' + uuid);
  };

  return (
    <GridColumn width={4}>
      <BpCard
        className='mb-7'
        fixedHeight='432px'
        noPadding
        canScroll
        header={{ headline: t('courses.title'), subHeadline: profileCourses?.length ?? 0 }}
      >
        <CoursesPreviewTable query={data} onNavigate={handleCourseNavigate} isCourseEditor={isCourseEditor} />
      </BpCard>
      <BpCard
        fixedHeight='432px'
        noPadding
        canScroll
        header={{ headline: t('groups.title'), subHeadline: profileGroups?.length ?? 0 }}
      >
        <GroupsPreviewTable onNavigate={handleGroupNavigate} query={data} />
      </BpCard>
    </GridColumn>
  );
}
